/**
 * Sending this trip's health record to the traveller's own doctor.
 *
 * The extent of the disclosure is shown before the address field is: what
 * leaves is a person's medications, measurements, laboratory values,
 * vaccinations, conditions and allergies, and they should see the size of that
 * before they type where it goes. The counts come from the same builder the
 * server sends with, so the preview cannot drift from the payload.
 *
 * One remembered address, not an address book: the traveller's own doctor,
 * entered by them, kept so they do not retype it before every trip and deleted
 * outright the moment they ask. A list of other people's doctors would be a
 * second thing to leak; their own single address is what makes the feature
 * usable at all.
 */

import {
  DoctorContact,
  forgetDoctorContact,
  getDoctorContact,
  getTripHealthSummary,
  saveDoctorContact,
  sendTripHealthSummary,
  TripSummaryPreview,
} from "@/services/api/travels";
import { Send, Stethoscope } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

interface DoctorSummarySenderProps {
  travelId: string;
}

const MAX_NOTE = 500;

export const DoctorSummarySender: React.FC<DoctorSummarySenderProps> = ({
  travelId,
}) => {
  const [preview, setPreview] = useState<TripSummaryPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState<DoctorContact | null>(null);
  const [remember, setRemember] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [summary, doctor] = await Promise.all([
        getTripHealthSummary(travelId),
        // A traveller who has not saved one yet is the normal case, not a
        // failure: the send form works either way.
        getDoctorContact().catch(() => null),
      ]);
      setPreview(summary);
      if (doctor) {
        setSaved(doctor);
        setEmail(doctor.email);
        setName(doctor.name ?? "");
      }
    } catch {
      // The panel is rendered inside a PREMIUM gate, so the usual reason to
      // land here is a network failure rather than entitlement. Leaving the
      // preview null shows the "nothing to send yet" state, which is honest
      // about what we know.
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [travelId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;

    try {
      setSending(true);
      const result = await sendTripHealthSummary(travelId, {
        recipientEmail: email.trim(),
        ...(name.trim() ? { recipientName: name.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(`Sent ${result.resources} records to ${result.sentTo}`);

      if (remember) {
        try {
          setSaved(
            await saveDoctorContact({
              email: email.trim(),
              ...(name.trim() ? { name: name.trim() } : {}),
            }),
          );
        } catch {
          // Remembering is a convenience. Failing at it must not read as a
          // failure to send, which has already happened.
        }
      }
      // The address stays in the field: the next thing a traveller does is
      // usually check it went to the right person, not send another.
      setNote("");
    } catch (error: any) {
      const code = error?.response?.data?.error?.code;
      if (code === "SUMMARY_EMPTY") {
        toast.error("Nothing was recorded for this trip yet");
      } else if (code === "INVALID_RECIPIENT") {
        toast.error("That does not look like an email address");
      } else {
        toast.error("The summary could not be sent — nothing was delivered");
      }
    } finally {
      setSending(false);
    }
  };

  const counts = preview?.counts;
  const rows: { label: string; value: number }[] = counts
    ? [
        { label: "Medications", value: counts.medications },
        { label: "Vital signs", value: counts.vitalSigns },
        { label: "Laboratory values", value: counts.labResults },
        { label: "Immunisations", value: counts.immunizations },
        { label: "Conditions", value: counts.conditions },
        { label: "Allergies", value: counts.allergies },
      ]
    : [];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <Stethoscope className="h-5 w-5 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Send this trip to your doctor
        </h3>
      </div>

      <p className="mb-4 text-sm text-gray-600">
        A PDF to read and a FHIR file to import, covering this trip only.
        Vaccinations from the 90 days before departure are included; your
        conditions and allergies go with it as standing context.
      </p>

      {loading ? (
        <div className="h-20 animate-pulse rounded-lg bg-gray-100" />
      ) : preview?.empty ? (
        <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
          Nothing has been recorded for these dates yet, so there is nothing to
          send.
        </p>
      ) : (
        <>
          <dl className="mb-5 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            {rows.map(({ label, value }) => (
              <div key={label} className="flex justify-between gap-2">
                <dt className="text-gray-500">{label}</dt>
                <dd className="font-medium text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>

          <form onSubmit={handleSend} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="doctor@practice.example"
                aria-label="Doctor's email address"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Their name (optional)"
                aria-label="Doctor's name"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
              />
            </div>
            <textarea
              value={note}
              maxLength={MAX_NOTE}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A note for them (optional)"
              aria-label="Note to the doctor"
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
            />
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Remember this address for my next trip
            </label>

            {saved && (
              <p className="text-xs text-gray-500">
                Saved: {saved.email}
                <button
                  type="button"
                  onClick={async () => {
                    await forgetDoctorContact();
                    setSaved(null);
                    setEmail("");
                    setName("");
                    toast.success("Address forgotten");
                  }}
                  className="ml-2 font-medium text-primary-600 underline"
                >
                  Forget it
                </button>
              </p>
            )}

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Sent once, when you press this. Nothing is ever sent
                automatically.
              </p>
              <button
                type="submit"
                disabled={sending || !email.trim()}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {sending ? "Sending…" : "Send summary"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
